import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { legacyData } from '../mockData';
import { Button } from '../components/ui/button';

const Legacy = () => {
  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Your Legacy | Preserve Your Land While Generating Income</title>
        <meta name="description" content="Turn rocky outcrops and ridgelines into profitable glamping sites. Use just 1% of your land to fund estate maintenance while preserving your family legacy." />
        <link rel="canonical" href="https://goglamping.co.za/legacy" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-stone-100 to-sage-50" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-stone-900 mb-6">
              {legacyData.title}
            </h1>
            <p className="text-2xl text-sage-800 font-semibold mb-4">
              {legacyData.subtitle}
            </p>
            <p className="text-xl text-stone-600 max-w-4xl mx-auto leading-relaxed">
              {legacyData.description}
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {legacyData.stats.map((stat) => (
              <div
                key={stat.id}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-stone-200"
              >
                <div className="text-5xl font-bold text-sage-700 mb-3">
                  {stat.value}
                </div>
                <div className="text-xl font-semibold text-stone-900 mb-2">
                  {stat.label}
                </div>
                <p className="text-stone-600 leading-relaxed">
                  {stat.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Multi-Generational Engine */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-6">
                The Multi-Generational Engine
              </h2>
              <p className="text-xl text-stone-600 mb-8 leading-relaxed">
                Frame your glamping project as a way to fund estate maintenance, taxes, and security through 1% of the land's footprint, ensuring the property remains in the family for generations.
              </p>
              
              <div className="space-y-4 mb-8">
                {legacyData.features.map((feature) => (
                  <div key={feature.id} className="flex items-start space-x-3">
                    <div className="bg-sage-700 rounded-full p-1 mt-1">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <p className="text-stone-700 text-lg">{feature.text}</p>
                  </div>
                ))}
              </div>

              <Link to="/sanctuary">
                <Button size="lg" className="bg-sage-700 hover:bg-sage-800 text-white px-6 py-5 text-base rounded-lg shadow-lg hover:scale-105 transition-all duration-300">
                  Discover The Sanctuary
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img
                src={legacyData.image}
                alt="Beautiful land with potential"
                className="w-full h-[500px] object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/50 to-transparent rounded-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Preservation Focus */}
      <section className="py-24 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Preservation is Our Promise
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Because the structures "touch the earth lightly" using no impact adjustable leg foundations, the land can return to its natural state in 30 days if ever moved. Your property's integrity remains untouched while generating substantial returns.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20">
                <h3 className="text-2xl font-bold text-sage-400 mb-4">Zero Scarring</h3>
                <p className="text-gray-300 leading-relaxed">
                  No concrete foundations. No permanent structures. Just elegant, removable architecture that respects your land's heritage.
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20">
                <h3 className="text-2xl font-bold text-sage-400 mb-4">Natural Recovery</h3>
                <p className="text-gray-300 leading-relaxed">
                  Indigenous flora and water tables remain protected. Your bi-annual pristine audits ensure your land stays untouched.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Unseen Business */}
      <section className="py-24 bg-gradient-to-br from-sage-50 to-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-6">
              The "Unseen" Business Model
            </h2>
            <p className="text-xl text-stone-600 max-w-3xl mx-auto">
              The beauty of this model is its discreet footprint. Your primary operations continue while this runs in the background.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-stone-900 mb-4">
                Turn "Waste Land" Into Gold
              </h3>
              <p className="text-stone-600 leading-relaxed mb-4">
                Those rocky outcrops, steep river banks, and deep woods that can't be farmed? They're about to become your most profitable per-square-meter acreage.
              </p>
              <p className="text-sage-700 font-semibold text-lg">
                Your "unusable" land now generates around R650,000 annually per unit
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-stone-900 mb-4">
                Complete Privacy Maintained
              </h3>
              <p className="text-stone-600 leading-relaxed mb-4">
                Guest access is carefully curated to designated areas only. The rest of your estate remains your private sanctuary.
              </p>
              <p className="text-sage-700 font-semibold text-lg">
                You maintain control while the land generates passive income
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-sage-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            You've Spent Years Protecting This Land
          </h2>
          <p className="text-xl mb-8 text-sage-100">
            It's time the land took care of you
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-sage-700 hover:bg-gray-100 px-8 py-6 text-lg rounded-lg shadow-2xl hover:scale-105 transition-all duration-300">
              Schedule Your Site Discovery Walk
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Legacy;
